import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import React from "react";
import { mergeRefs } from "react-merge-refs";
import { useAutofocus } from "../hooks/useAutofocus";
import { useAutosizeTextarea } from "../hooks/useAutoresize";
import useOpen from "../hooks/useOpen";
import { usePermissionDeniedModal } from "../hooks/usePermissionDeniedModal";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { onDeviceWithNotch } from "../utils/styled";
import { SEND_MESSAGE_SVG, TIMES_SVG } from "../utils/svgs";
import { CAMERA_ICON } from "../utils/svgs_new";
import ChooseAttachmentBottomSheet from "./ChooseAttachmentBottomSheet";
import { AppIcon } from "./Generic/AppIcon";

interface Props {
  value: string;
  disabled: boolean;
  inputRef: React.Ref<HTMLTextAreaElement>;
  images: string[];
  onClickRemoveImages: (image: string) => void;
  onSelectImages: (photos: string[]) => void;
  onChangeValue: (value: string) => void;
  onSubmit: () => void;
}

const ChatRoomInputPanel = (props: Props) => {
  const bottomSheet = useOpen();
  const innerInputRef = React.useRef<HTMLTextAreaElement>(null);
  const [imagesAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();
  const { openModal: openPermissionDeniedModal } = usePermissionDeniedModal();

  useAutosizeTextarea(innerInputRef);
  useAutofocus(innerInputRef, { timeout: 400 });

  const inputRefs = mergeRefs([props.inputRef, innerInputRef]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    props.onChangeValue(e.target.value);
  };

  React.useEffect(() => {
    if (Capacitor.isPluginAvailable("Keyboard")) {
      Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    }
  }, []);

  const handleClickCamera = async () => {
    const { camera: cameraPermission } = await Camera.checkPermissions();

    if (cameraPermission === "denied") {
      bottomSheet.actions.dismiss();
      openPermissionDeniedModal("camera");
      return;
    }

    const photo = await Camera.getPhoto({
      source: CameraSource.Camera,
      resultType: CameraResultType.Uri,
      correctOrientation: true,
      quality: 90,
    });

    bottomSheet.actions.dismiss();

    props.onSelectImages([photo.webPath as string]);
  };

  const handleClickGallery = async () => {
    const { photos: photosPermission } = await Camera.checkPermissions();

    if (photosPermission === "denied") {
      bottomSheet.actions.dismiss();
      openPermissionDeniedModal("photos");
      return;
    }

    const { photos } = await Camera.pickImages({
      width: 1024,
      height: 1024,
      correctOrientation: true,
      quality: 90,
      limit: 5,
    });

    bottomSheet.actions.dismiss();

    props.onSelectImages(photos.slice(0, 5).map((photo) => photo.webPath));
  };

  const placeholder = !props.disabled
    ? translate(STRINGS.TYPE_MESSAGE) + "..."
    : translate(STRINGS.CHAT_NOT_ACTIVE_TITLE);

  const canSubmitButton = !props.disabled && (props.value.length > 0 || props.images.length > 0);

  return (
    <ChatRoomInputPanel.Root>
      <ChatRoomInputPanel.PanelWrapper>
        <ChatRoomInputPanel.WhitePanel>
          <ChatRoomInputPanel.Attachments
            ref={imagesAutoAnimateRef}
            hidden={props.images.length === 0}
          >
            {props.images.map((image) => (
              <ChatRoomInputPanel.ImageWrapper key={image}>
                <ChatRoomInputPanel.Image src={image} />
                <ChatRoomInputPanel.RemoveButton onClick={() => props.onClickRemoveImages(image)}>
                  <AppIcon icon={TIMES_SVG} stroke="currentColor" size={8} />
                </ChatRoomInputPanel.RemoveButton>
              </ChatRoomInputPanel.ImageWrapper>
            ))}
          </ChatRoomInputPanel.Attachments>
          <ChatRoomInputPanel.BottomWhitePanel>
            <ChatRoomInputPanel.TextAreaWrapper>
              <ChatRoomInputPanel.TextArea
                ref={inputRefs}
                value={props.value}
                placeholder={placeholder}
                disabled={props.disabled}
                rows={1}
                onChange={handleChange}
              />
            </ChatRoomInputPanel.TextAreaWrapper>
            <ChatRoomInputPanel.Options>
              <ChatRoomInputPanel.OptionButton onClick={bottomSheet.actions.open}>
                <AppIcon icon={CAMERA_ICON} />
              </ChatRoomInputPanel.OptionButton>
            </ChatRoomInputPanel.Options>
          </ChatRoomInputPanel.BottomWhitePanel>
        </ChatRoomInputPanel.WhitePanel>
        <ChatRoomInputPanel.SendButton disabled={!canSubmitButton} onClick={props.onSubmit}>
          <AppIcon icon={SEND_MESSAGE_SVG} fill="white" />
        </ChatRoomInputPanel.SendButton>
      </ChatRoomInputPanel.PanelWrapper>
      <ChooseAttachmentBottomSheet
        isOpen={bottomSheet.isOpen}
        onClickCamera={handleClickCamera}
        onClickGallery={handleClickGallery}
        onRequestClose={bottomSheet.actions.dismiss}
      />
    </ChatRoomInputPanel.Root>
  );
};

ChatRoomInputPanel.Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;

  ${onDeviceWithNotch(css`
    padding: 16px 16px 24px;
  `)}
`;

ChatRoomInputPanel.Attachments = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  overflow: auto;
  max-width: calc(80px * 3.5);
  border-radius: 16px;
  margin: 8px 8px 0;
  width: 100%;

  &[hidden] {
    margin: 0;
  }

  &:not([hidden]) + * {
    margin-top: 8px;
    border-top: 1px solid var(--gray-100);
  }
`;

ChatRoomInputPanel.ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: fit-content;
`;

ChatRoomInputPanel.Image = styled.img`
  --size: 72px;
  width: var(--size);
  height: var(--size);
  object-fit: cover;
  border-radius: 16px;
`;

ChatRoomInputPanel.RemoveButton = styled.button`
  --size: 22px;
  background-color: white;
  color: #888;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 6px;
  right: 6px;
  width: var(--size);
  height: var(--size);
  flex-shrink: 0;

  .AppIcon {
    flex-shrink: 0;
    padding: 0;
  }
`;

ChatRoomInputPanel.PanelWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

ChatRoomInputPanel.WhitePanel = styled.div`
  flex: 1;
  background-color: #fff;
  border-radius: 25px;
  border: 1px solid var(--gray-200);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  transition: all 150ms ease;

  &:focus-within {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 1px var(--primary-color);
  }
`;

ChatRoomInputPanel.BottomWhitePanel = styled.div`
  display: flex;
  align-items: flex-end;
`;

ChatRoomInputPanel.TextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 16px 0 16px 16px;
  flex: 1;
`;

ChatRoomInputPanel.TextArea = styled.textarea`
  border: none;
  background-color: transparent;
  padding: 0;
  font-size: 16px;
  line-height: 1.1;
  resize: none;
`;

ChatRoomInputPanel.Options = styled.div`
  display: flex;
  gap: 8px;
  height: 52px;
  padding: 2px;
  box-sizing: border-box;
`;

ChatRoomInputPanel.OptionButton = styled.button`
  --size: 48px;
  border: none;
  background-color: transparent;
  padding: 0;
  width: var(--size);
  height: var(--size);
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;

  path {
    fill: var(--gray-500);
  }

  svg {
    width: 22px;
    height: 22px;
  }

  &:hover {
    background-color: var(--gray-50);
    path {
      fill: black;
    }
  }
`;

ChatRoomInputPanel.SendButton = styled.button`
  --size: 54px;
  background-color: var(--primary-color);
  border: none;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: var(--size);
  height: var(--size);
  border-radius: var(--size);
  transition: all 150ms ease;

  &:disabled {
    background-color: var(--gray-200);
  }

  svg {
    transform: translateX(1px);
  }
`;

export default ChatRoomInputPanel;
